import React, { useEffect, useState } from 'react';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Banknote,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  Scale,
  Store,
  TrendingUp,
  User,
  Wallet,
  XCircle,
} from 'lucide-react';
import FormModal from '../../Modals/FormModal';
import AdminCashMonitorService from '../../../services/cash/AdminCashMonitorService';
import {
  AdminCashCountDTO,
  AdminCashLastClosureDTO,
  AdminCashPaymentSummaryDTO,
  AdminCashRegisterMonitorDTO,
} from '../../../types/cash/adminCashMonitor';
import { AdminCashRegisterHistoryItemDTO } from '../../../types/cash/adminCashMonitorHistory';
import { formatCurrency } from '../../../utils/formatCurrency';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';

interface Props {
  register: AdminCashRegisterMonitorDTO | null;
  onClose: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Abierta',
  CLOSED: 'Cerrada',
  NEVER_OPENED: 'Sin uso',
};

const CASH_COUNT_STATUS_LABEL: Record<string, string> = {
  STARTED: 'En proceso',
  COMPLETED: 'Finalizado',
};

const dateTime = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleString('es-SV', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/* ===================================================== */
/* 🔹 Bloques reutilizables (mismo lenguaje visual que     */
/*    CashSummaryView / CashSessionInfoView del módulo)    */
/* ===================================================== */

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, badge, children }) => (
  <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
    <div className="flex items-center justify-between border-b border-stroke px-5 py-3 dark:border-strokedark">
      <h3 className="flex items-center gap-2 text-sm font-medium text-black dark:text-white">
        <span className="text-primary">{icon}</span>
        {title}
      </h3>
      {badge}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InfoItem: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-3">
    <div className="text-primary/80">{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="truncate text-sm font-medium text-black dark:text-white">{value}</p>
    </div>
  </div>
);

const AmountRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | null | undefined;
  tone?: 'success' | 'danger' | 'neutral';
  prefix?: string;
}> = ({ icon, label, value, tone = 'neutral', prefix = '$' }) => {
  const color =
    tone === 'danger'
      ? 'text-danger'
      : tone === 'success'
      ? 'text-success'
      : 'text-black dark:text-white';

  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <span className={color}>{icon}</span>
        {label}
      </span>
      <span className={`text-sm font-medium ${color}`}>
        {prefix}
        {formatCurrency(value)}
      </span>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    OPEN: 'bg-success/10 text-success',
    CLOSED: 'bg-danger/10 text-danger',
    NEVER_OPENED: 'bg-gray-2 text-bodydark2 dark:bg-meta-4 dark:text-bodydark',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
        styles[status] ?? styles.NEVER_OPENED
      }`}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
};

const DifferenceBadge: React.FC<{ value: number | null | undefined }> = ({ value }) => {
  const num = Number(value ?? 0);
  const isBalanced = num === 0;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold ${
        isBalanced ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
      }`}
    >
      {isBalanced ? <CheckCircle2 size={12} /> : <XCircle size={12} />}${formatCurrency(num)}
    </span>
  );
};

const PaymentMethodsTable: React.FC<{ payments: AdminCashPaymentSummaryDTO[] }> = ({ payments }) => (
  <div className="rounded-sm border border-stroke dark:border-strokedark">
    {payments.map((p, i) => (
      <div
        key={p.method}
        className={`flex items-center justify-between px-4 py-2.5 text-sm ${
          i !== payments.length - 1 ? 'border-b border-stroke dark:border-strokedark' : ''
        }`}
      >
        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <CreditCard size={14} className="text-primary/80" />
          {p.method}
        </span>
        <span className="font-medium text-black dark:text-white">${formatCurrency(p.amount)}</span>
      </div>
    ))}
  </div>
);

const ClosureCard: React.FC<{ closure: AdminCashLastClosureDTO }> = ({ closure }) => (
  <SectionCard
    icon={<FileText size={16} />}
    title={`Corte ${closure.closureType} · ${closure.closureNumber}`}
    badge={<DifferenceBadge value={closure.difference} />}
  >
    <div className="divide-y divide-stroke dark:divide-strokedark">
      <AmountRow icon={<TrendingUp size={14} />} label="Total ventas" value={closure.totalSales} />
      <AmountRow icon={<Wallet size={14} />} label="Efectivo esperado" value={closure.expectedCash} />
      <AmountRow icon={<Banknote size={14} />} label="Efectivo contado" value={closure.countedCash} />
    </div>
    {closure.observations && (
      <p className="mt-3 rounded bg-gray-2 p-2 text-xs text-gray-500 dark:bg-meta-4 dark:text-gray-400">
        {closure.observations}
      </p>
    )}
    {closure.payments.length > 0 && (
      <div className="mt-4">
        <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
          Formas de pago
        </p>
        <PaymentMethodsTable payments={closure.payments} />
      </div>
    )}
  </SectionCard>
);

const CashCountCard: React.FC<{ count: AdminCashCountDTO | null }> = ({ count }) => (
  <SectionCard
    icon={<Scale size={16} />}
    title="Último arqueo"
    badge={count ? <DifferenceBadge value={count.difference} /> : undefined}
  >
    {!count ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No se ha registrado ningún arqueo en esta sesión.
      </p>
    ) : (
      <>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <InfoItem icon={<Clock size={16} />} label="Fecha" value={dateTime(count.countedAt)} />
          <InfoItem icon={<User size={16} />} label="Realizado por" value={count.countedByName ?? '—'} />
          <InfoItem
            icon={<CheckCircle2 size={16} />}
            label="Estado"
            value={CASH_COUNT_STATUS_LABEL[count.status] ?? count.status}
          />
          <InfoItem icon={<FileText size={16} />} label="N.° de arqueo" value={count.countNumber} />
        </div>
        <div className="divide-y divide-stroke border-t border-stroke pt-2 dark:divide-strokedark dark:border-strokedark">
          <AmountRow icon={<Wallet size={14} />} label="Efectivo esperado" value={count.expectedAmount} />
          <AmountRow icon={<Banknote size={14} />} label="Efectivo contado" value={count.countedAmount} />
        </div>
        {count.observations && (
          <p className="mt-3 rounded bg-gray-2 p-2 text-xs text-gray-500 dark:bg-meta-4 dark:text-gray-400">
            {count.observations}
          </p>
        )}
      </>
    )}
  </SectionCard>
);

const AdminCashRegisterMonitorModal: React.FC<Props> = ({ register, onClose }) => {
  const { handle } = useApiErrorHandler();

  const [history, setHistory] = useState<AdminCashRegisterHistoryItemDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!register) {
      setHistory([]);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const data = await AdminCashMonitorService.getRegisterHistory(register.registerId);
        if (!cancelled) setHistory(data);
      } catch (error) {
        if (!cancelled) handle(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [register]);

  const {
    registerName = '',
    registerCode = '',
    branchName = '',
    status = 'NEVER_OPENED',
    sessionNumber,
    openedAt,
    closedAt,
    openedByName,
    closedByName,
    openingAmount,
    liveSummary,
    livePayments,
    lastClosure,
    lastCashCount,
  } = register ?? ({} as AdminCashRegisterMonitorDTO);

  return (
    <FormModal
      isOpen={register !== null}
      onClose={onClose}
      title={register ? `${registerName} (${registerCode})` : ''}
      maxWidth="max-w-3xl"
    >
      <div className="flex flex-col gap-5">
        {/* 🔹 Encabezado: sucursal + estado */}
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Store size={16} className="text-primary/80" />
            {branchName}
          </span>
          <StatusBadge status={status} />
        </div>

        {status === 'NEVER_OPENED' && (
          <div className="rounded-sm border border-stroke bg-white p-6 text-center shadow-default dark:border-strokedark dark:bg-boxdark">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Esta terminal nunca ha tenido una sesión de caja abierta.
            </p>
          </div>
        )}

        {status === 'OPEN' && (
          <div className="flex flex-col gap-4">
            <SectionCard icon={<Store size={16} />} title="Sesión de caja">
              <div className="grid grid-cols-2 gap-4">
                <InfoItem icon={<FileText size={16} />} label="Sesión" value={sessionNumber} />
                <InfoItem icon={<User size={16} />} label="Abierta por" value={openedByName ?? '—'} />
                <InfoItem icon={<Clock size={16} />} label="Apertura" value={dateTime(openedAt)} />
                <InfoItem
                  icon={<Wallet size={16} />}
                  label="Monto inicial"
                  value={`$${formatCurrency(openingAmount)}`}
                />
              </div>
            </SectionCard>

            {liveSummary && (
              <SectionCard
                icon={<TrendingUp size={16} />}
                title="Corte X en vivo"
                badge={
                  <span className="text-xs font-semibold text-black dark:text-white">
                    ${formatCurrency(liveSummary.expectedAmount)}
                  </span>
                }
              >
                <div className="divide-y divide-stroke dark:divide-strokedark">
                  <AmountRow
                    icon={<TrendingUp size={14} />}
                    label="Ventas en efectivo"
                    value={liveSummary.salesAmount}
                    tone="success"
                  />
                  <AmountRow
                    icon={<ArrowUpCircle size={14} />}
                    label="Ingresos"
                    value={liveSummary.incomeAmount}
                    tone="success"
                  />
                  <AmountRow
                    icon={<ArrowDownCircle size={14} />}
                    label="Retiros"
                    value={liveSummary.withdrawalAmount}
                    tone="danger"
                  />
                  <AmountRow
                    icon={<ArrowDownCircle size={14} />}
                    label="Devoluciones"
                    value={liveSummary.refundAmount}
                    tone="danger"
                  />
                  <AmountRow
                    icon={<ArrowUpCircle size={14} />}
                    label="Ajustes positivos"
                    value={liveSummary.adjustmentInAmount}
                    tone="success"
                  />
                  <AmountRow
                    icon={<ArrowDownCircle size={14} />}
                    label="Ajustes negativos"
                    value={liveSummary.adjustmentOutAmount}
                    tone="danger"
                  />
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-stroke pt-3 dark:border-strokedark">
                  <span className="text-sm font-semibold text-black dark:text-white">
                    Efectivo esperado
                  </span>
                  <span className="text-lg font-bold text-primary">
                    ${formatCurrency(liveSummary.expectedAmount)}
                  </span>
                </div>
              </SectionCard>
            )}

            {livePayments && livePayments.length > 0 && (
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                  <CreditCard size={14} /> Formas de pago
                </p>
                <PaymentMethodsTable payments={livePayments} />
              </div>
            )}

            <CashCountCard count={lastCashCount} />
          </div>
        )}

        {status === 'CLOSED' && (
          <div className="flex flex-col gap-4">
            <SectionCard icon={<Store size={16} />} title="Última sesión">
              <div className="grid grid-cols-2 gap-4">
                <InfoItem icon={<FileText size={16} />} label="Sesión" value={sessionNumber} />
                <InfoItem icon={<User size={16} />} label="Cerrada por" value={closedByName ?? '—'} />
                <InfoItem icon={<Clock size={16} />} label="Apertura" value={dateTime(openedAt)} />
                <InfoItem icon={<Clock size={16} />} label="Cierre" value={dateTime(closedAt)} />
              </div>
            </SectionCard>

            {lastClosure && <ClosureCard closure={lastClosure} />}

            <CashCountCard count={lastCashCount} />
          </div>
        )}

        {/* 🔹 Historial */}
        <div>
          <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
            <Calendar size={14} /> Historial de la caja
          </p>

          <div className="overflow-hidden rounded-sm border border-stroke dark:border-strokedark">
            {loading && (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400">Cargando historial...</p>
            )}

            {!loading && history.length === 0 && (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
                No hay sesiones registradas para esta caja.
              </p>
            )}

            {!loading && history.length > 0 && (
              <div className="max-h-72 overflow-x-auto overflow-y-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="sticky top-0 bg-gray-2 dark:bg-meta-4">
                    <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                      <th className="px-4 py-2">Sesión</th>
                      <th className="px-2 py-2">Apertura</th>
                      <th className="px-2 py-2">Cierre</th>
                      <th className="px-2 py-2">Estado</th>
                      <th className="px-2 py-2">Arqueo</th>
                      <th className="px-4 py-2 text-right">Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr
                        key={item.sessionId}
                        className="border-t border-stroke dark:border-strokedark"
                      >
                        <td className="px-4 py-2.5 font-medium text-black dark:text-white">
                          {item.sessionNumber}
                        </td>
                        <td className="px-2 py-2.5 text-gray-600 dark:text-gray-400">
                          {dateTime(item.openedAt)}
                        </td>
                        <td className="px-2 py-2.5 text-gray-600 dark:text-gray-400">
                          {dateTime(item.closedAt)}
                        </td>
                        <td className="px-2 py-2.5">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="px-2 py-2.5">
                          {item.cashCount ? (
                            <DifferenceBadge value={item.cashCount.difference} />
                          ) : (
                            <span className="text-xs text-gray-400">No</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {item.closure ? (
                            <DifferenceBadge value={item.closure.difference} />
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </FormModal>
  );
};

export default AdminCashRegisterMonitorModal;
